export type ReferenceSourceId = 'HC2024' | 'ADA2026' | 'LOCAL_AUDIT' | 'LOCAL_WORKFLOW'
export type RuleReferenceId =
  | 'diagnosis'
  | 'ketones'
  | 'hhs_guardrails'
  | 'resolution'
  | 'insulin_iv'
  | 'fluids'
  | 'potassium'
  | 'transition'
  | 'correction_audit'
  | 'guided_workflow'

export interface ReferenceSource {
  id: ReferenceSourceId
  kind: 'clinical' | 'methodology'
  shortTitle: string
  citation: string
  year: number
  doi?: string
  url?: string
}

export interface RuleReference {
  id: RuleReferenceId
  label: string
  description: string
  sourceIds: ReferenceSourceId[]
  implementationVersion: string
  verifiedOn: string
}

export const referenceSources: Record<ReferenceSourceId, ReferenceSource> = {
  HC2024: {
    id: 'HC2024',
    kind: 'clinical',
    shortTitle: 'Consensus crisi iperglicemiche 2024',
    citation: 'Umpierrez GE, Davis GM, ElSayed NA, et al. Hyperglycemic Crises in Adults With Diabetes: A Consensus Report. Diabetes Care. 2024;47(8):1257–1275.',
    year: 2024,
    doi: '10.2337/dci24-0032',
    url: 'https://doi.org/10.2337/dci24-0032'
  },
  ADA2026: {
    id: 'ADA2026',
    kind: 'clinical',
    shortTitle: 'ADA Standards of Care 2026 · Hospital',
    citation: 'American Diabetes Association Professional Practice Committee for Diabetes. 16. Diabetes Care in the Hospital: Standards of Care in Diabetes—2026. Diabetes Care. 2026;49(Suppl 1):S339–S355.',
    year: 2026,
    doi: '10.2337/dc26-S016',
    url: 'https://doi.org/10.2337/dc26-S016'
  },
  LOCAL_AUDIT: {
    id: 'LOCAL_AUDIT',
    kind: 'methodology',
    shortTitle: 'Metodologia interna di correzione e audit',
    citation: 'Scelta progettuale del prototipo: conservazione del dato precedente, ricostruzione deterministica dell’episodio e invalidazione delle conclusioni dipendenti da dati corretti.',
    year: 2026
  },
  LOCAL_WORKFLOW: {
    id: 'LOCAL_WORKFLOW',
    kind: 'methodology',
    shortTitle: 'Metodologia interna del percorso guidato',
    citation: 'Scelta progettuale del prototipo: presentazione progressiva dello stesso motore deterministico in fasi operative, con dashboard completa conservata come dettaglio secondario.',
    year: 2026
  }
}

export const ruleReferences: Record<RuleReferenceId, RuleReference> = {
  diagnosis: {
    id: 'diagnosis',
    label: 'Diagnosi DKA / HHS / forma mista',
    description: 'Criteri diagnostici e classificazione del quadro metabolico.',
    sourceIds: ['HC2024'],
    implementationVersion: '0.4.2',
    verifiedOn: '2026-08-21'
  },
  ketones: {
    id: 'ketones',
    label: 'β-OHB, chetonuria e indisponibilità dei chetoni',
    description: 'β-OHB preferenziale, chetonuria come fallback diagnostico e nessuna risoluzione automatica basata sulla chetonuria.',
    sourceIds: ['HC2024'],
    implementationVersion: '0.4.2',
    verifiedOn: '2026-08-21'
  },
  hhs_guardrails: {
    id: 'hhs_guardrails',
    label: 'Guardrail di correzione nel percorso iperosmolare',
    description: 'Velocità di riduzione di glicemia e osmolalità e monitoraggio del percorso HHS/iperosmolare.',
    sourceIds: ['HC2024'],
    implementationVersion: '0.4.2',
    verifiedOn: '2026-08-21'
  },
  resolution: {
    id: 'resolution',
    label: 'Criteri di risoluzione DKA / HHS',
    description: 'Criteri biochimici e clinici utilizzati per proporre la conferma di risoluzione; nello stato candidato non viene riattivato automaticamente il riferimento insulinico iniziale.',
    sourceIds: ['HC2024'],
    implementationVersion: '0.5.0',
    verifiedOn: '2026-08-25'
  },
  insulin_iv: {
    id: 'insulin_iv',
    label: 'Insulina EV e fase con glucosio',
    description: 'Riferimenti 0,1/0,05 U/kg/h, HHS 0,05 U/kg/h e associazione di glucosio EV nella DKA non risolta con glicemia ridotta; nessuna risalita automatica alla dose iniziale quando i criteri di risoluzione sono raggiunti.',
    sourceIds: ['HC2024'],
    implementationVersion: '0.5.0',
    verifiedOn: '2026-08-25'
  },
  fluids: {
    id: 'fluids',
    label: 'Fluidoterapia',
    description: 'Cristalloidi iniziali, velocità di riferimento e individualizzazione nei pazienti a rischio di sovraccarico.',
    sourceIds: ['HC2024'],
    implementationVersion: '0.4.2',
    verifiedOn: '2026-08-21'
  },
  potassium: {
    id: 'potassium',
    label: 'Gestione del potassio',
    description: 'Soglie per rinvio dell’insulina, supplementazione, target e monitoraggio seriale del K.',
    sourceIds: ['HC2024'],
    implementationVersion: '0.4.2',
    verifiedOn: '2026-08-21'
  },
  transition: {
    id: 'transition',
    label: 'Transizione EV → SC',
    description: 'Stima della dose sottocutanea, uso del fabbisogno EV documentato e sovrapposizione con la basale prima dello stop EV.',
    sourceIds: ['HC2024', 'ADA2026'],
    implementationVersion: '0.5.0',
    verifiedOn: '2026-08-25'
  },
  correction_audit: {
    id: 'correction_audit',
    label: 'Correzione delle rilevazioni e audit',
    description: 'Il dato precedente viene conservato; la cronologia è ricostruita e le conclusioni cliniche dipendenti dai dati vengono invalidate.',
    sourceIds: ['LOCAL_AUDIT'],
    implementationVersion: '0.4.2',
    verifiedOn: '2026-08-21'
  },
  guided_workflow: {
    id: 'guided_workflow',
    label: 'Percorso clinico progressivo',
    description: 'Lo stesso motore viene presentato in fasi sequenziali: inquadramento, stabilizzazione, monitoraggio, risoluzione e transizione; timeline, audit e fonti restano consultabili nei dettagli.',
    sourceIds: ['LOCAL_WORKFLOW'],
    implementationVersion: '0.5.0',
    verifiedOn: '2026-08-25'
  }
}

export const allRuleReferenceIds = Object.keys(ruleReferences) as RuleReferenceId[]
